import type { LetterStatus } from './LetterStatus'
import type { LetterProviderValue } from '@/Ship/Contracts/LetterProvider'
import type { UserLike } from '@/Ship/Helpers/fieldViews'

/** Ключ ресурсу листа (`SPEC-002` §2.2): без сторінки, лише вкладка в картках. */
export const LETTER_RESOURCE = 'Communication.Letter'

/** Колекція листів (`SPEC-013` §8). */
export const LETTER_REST_URL = '/services/letters'

/** Крок 1 двокрокового завантаження вкладення листа (G-05). */
export const LETTER_FILE_UPLOAD_URL = '/services/letter_files'

/** Колекція шаблонів листів — кнопка «Відправити лист» бере з неї набір (§6.2). */
export const LETTER_TEMPLATE_REST_URL = '/services/letter_templates'

/** Рядків на сторінку вкладки (`SPEC-013` §9.2): як у легасі. */
export const LETTERS_PAGE_SIZE = 20

/**
 * Типи вкладень — білий список легасі (Q13.8 (а)), той самий, що в шаблонів
 * листів і договорів CRM: бек приймає їх за розширенням і вмістом.
 */
export const LETTER_FILE_EXTENSIONS = [
    'docx',
    'dotx',
    'xlsx',
    'xltx',
    'pptx',
    'ppsx',
    'potx',
    'sldx',
    'oxt',
    'pdf',
    'gif',
    'jpg',
    'jpeg',
    'jp2',
    'png',
    'webp',
    'heic',
]

/** Стеля — ядровий nginx (`SPEC-008` §5.7.3). */
export const LETTER_FILE_MAX_SIZE_MB = 10

/** Поля-адреси листа: три незалежні переліки (D-15). */
export const LETTER_ADDRESS_FIELDS = ['toWhom', 'cc', 'bcc'] as const

export type LetterAddressField = (typeof LETTER_ADDRESS_FIELDS)[number]

/**
 * Чий лист — поле власника в записі листа й фільтрі колекції (`SPEC-013`
 * §4.4, §8): клієнт, перевізник або контакт одного з них; етап 2
 * (`SPEC-025` §3.2) — заявка, запит чи тендер.
 */
export type LetterOwnerField = 'client' | 'carrier' | 'clientContact' | 'carrierContact' | 'application' | 'request' | 'tender'

/** Контрагент, у картці якого стоїть вкладка: від нього — адресати й набір шаблонів. */
export type LetterCounterparty = 'client' | 'carrier'

/**
 * Власник вкладки «Листи»: поле фільтра, провайдер шаблонів і звідки брати
 * адресатів. Адреси інших контейнерів — рядками, а не імпортом
 * (`SPEC-002` §2.1): лист знає лише REST-контракт власника.
 */
export interface LetterOwnerConfig {
    field: LetterOwnerField
    provider: LetterProviderValue
    counterparty: LetterCounterparty
}

export const LETTER_OWNERS: Record<LetterOwnerField, LetterOwnerConfig> = {
    client: { field: 'client', provider: 'client', counterparty: 'client' },
    carrier: { field: 'carrier', provider: 'carrier', counterparty: 'carrier' },
    clientContact: { field: 'clientContact', provider: 'client_contact', counterparty: 'client' },
    carrierContact: { field: 'carrierContact', provider: 'carrier_contact', counterparty: 'carrier' },
    // Адресати листа перевезення — з клієнта джерела (лист заявки має й перевізника)
    application: { field: 'application', provider: 'application', counterparty: 'client' },
    request: { field: 'request', provider: 'request', counterparty: 'client' },
    tender: { field: 'tender', provider: 'tender', counterparty: 'client' },
}

/**
 * Звідки адресати листа (`SPEC-013` §9.4): пошти контрагента й пошти його
 * контактів. Лише підказки поля — адресу можна вписати й вручну.
 */
export const LETTER_COUNTERPARTY_URLS: Record<LetterCounterparty, { owner: string; contacts: string }> = {
    client: { owner: '/services/clients', contacts: '/services/client_contacts' },
    carrier: { owner: '/services/carriers', contacts: '/services/carrier_contacts' },
}

/** Провайдер листа документа — за ресурсом картки документа (`SPEC-013` §9.5). */
export const DOCUMENT_LETTER_PROVIDERS: Record<string, LetterProviderValue> = {
    'Crm.ClientDocument': 'client_document',
    'Crm.CarrierDocument': 'carrier_document',
    'Transportation.ApplicationDocument': 'application_document',
}

/** Підписи — дослівно з легасі (`SPEC-013` §9.7), «Статус» — Q13.10. */
export const LETTER_LABELS = {
    toWhom: 'E-mail адреси для відправки',
    cc: 'E-mail адреси для відправки копій листів',
    bcc: 'E-mail адреси для відправки прихованих копій листів',
    scheduledAt: 'Дата та час відкладеної відправки',
    sentAt: 'Дата та час відправки',
    subject: 'Тема',
    body: 'Текст листа',
    attachments: 'Вкладення',
    status: 'Статус',
    author: 'Автор',
    createdAt: 'Дата створення',
} as const

/** Тексти кнопок, шухляд і повідомлень (`SPEC-013` §9.3–§9.5, §9.7). */
export const LETTER_TEXTS = {
    tab: 'Листи',
    sendLetter: 'Відправити лист',
    templatesLoading: 'Шаблони листа завантажуються',
    otherTemplates: 'Інші шаблони листа',
    /**
     * Постійне пояснення на місці кнопки «Відправити лист», коли шаблону за
     * замовчуванням немає (вичитка підказок 2026-10-14, №15): раніше це
     * казала лише підказка, яку можна сховати. Шаблони веде адміністратор
     * (`LetterTemplateRule::canManage`).
     */
    noDefaultTemplate: (providerLabel: string): string =>
        `Немає шаблону листа за замовчуванням. Кнопка «Відправити лист» з'явиться, коли адміністратор позначить ` +
        `шаблон із провайдером «${providerLabel}» як «За замовчуванням» у Довідниках → «Шаблони листів».`,
    menuGroup: 'Генерація документу',
    send: 'Відправити',
    drawerTitle: 'Лист',
    documentDrawerTitle: 'Відправити документи',
    close: 'Закрити',
    success: 'Успіх',
    error: 'Помилка',
    sentSuccess: 'Лист успішно відправлено.',
    fillToWhom: 'Заповніть поле "E-mail адреси для відправки"',
    sendFailed: 'Помилка відправки листа.',
    contactAdmin: 'Зверніться до адміністратора системи.',
    empty: 'Листів ще немає.',
    loadFailed: 'Не вдалося завантажити листи.',
    view: 'Переглянути',
    edit: 'Редагувати',
    remove: 'Видалити',
    removeConfirm: 'Ви дійсно хочете видалити?',
    yes: 'Так',
    no: 'Ні',
    actions: 'Дії з листом',
    addressPlaceholder: 'Адреса або контакт зі списку',
} as const

/** Файл листа так, як його віддає бек (`file:short:read` / `file:read` кроку 1). */
export interface LetterFile {
    '@id'?: string
    uuid?: string
    originalName?: string | null
    url?: string | null
    createdAt?: string
}

/** Власник чи контакт у записі листа — лише назва й адреса (`:short:read`). */
export interface LetterRelationRef {
    '@id'?: string
    uuid?: string
    title?: string | null
}

/**
 * Права листа — `_permissions` із `?permissions=true` (`SPEC-013` §11):
 * `update`, `delete`, `send` бек дає автору чи адміністратору й лише
 * невідправленому листу.
 */
export interface LetterPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    send?: boolean
}

/** Лист так, як його віддають список і картка (`letter:read` = `letter:collection:read`). */
export interface ILetter {
    '@id'?: string
    uuid?: string
    id?: number
    status?: LetterStatus | string | null
    subject?: string | null
    toWhom?: string[] | null
    cc?: string[] | null
    bcc?: string[] | null
    /** HTML tiptap, санітизований; без підпису (§4.4) */
    body?: string | null
    /** HTML, що фактично пішов (тіло + підпис), — лише в відправленого */
    sentBody?: string | null
    /** Причина останньої невдалої відправки (Q13.1) */
    sendError?: string | null
    scheduledAt?: string | null
    sentAt?: string | null
    author?: UserLike | null
    client?: LetterRelationRef | null
    carrier?: LetterRelationRef | null
    clientContact?: LetterRelationRef | null
    carrierContact?: LetterRelationRef | null
    application?: LetterRelationRef | null
    request?: LetterRelationRef | null
    tender?: LetterRelationRef | null
    /** Знімок прапорця шаблону «Офіційний лист заявки» (лише читання) */
    isOfficialApplication?: boolean
    attachments?: LetterFile[] | null
    createdAt?: string | null
    _permissions?: LetterPermissions
    [key: string]: unknown
}

/** Відповідь колекції ядра. */
export interface LetterCollectionResponse<T> {
    items?: T[]
    'hydra:member'?: T[]
    meta?: { total?: number }
}

/** Шаблон листа для кнопки «Відправити лист» (`letter_template:read`). */
export interface LetterTemplateOption {
    uuid: string
    id?: number
    title: string
    provider?: string
    isDefault?: boolean
    isOfficialApplication?: boolean
    sorting?: number
    clients?: LetterRelationRef[] | null
    carriers?: LetterRelationRef[] | null
}
